import { useAuthenticatedQuery } from '../useAuthenticatedQuery';
import axios from 'axios';
import { API_BASE_URL, FUNCTION_KEY, PROJECT_SCOPE } from '../../config';
import { useOrgContext } from '@datahooks/admin/organisation/AdminOrgIDContext.jsx';
import logError from '../metadata/ErrorLogging';
import PropTypes from 'prop-types';

/**
 * Fetches organisation-level event metrics (project/test level).
 * 
 * @function useOrganisationEventMetrics
 * @param {string} event_level - The level of the event ('Project' or 'Test').
 * @param {Object} [config={}] - Config options for metrics.
 * @param {number} [config.recent_days=7] - Number of days for recent activity.
 * @param {number} [config.top_projects_limit=99] - Limit for top projects.
 * @param {number} [config.trend_days=30] - Days for trend analysis.
 * 
 * @returns {{
 *   metrics: {
 *     basic_counts?: { total_events: number, active_events: number, closed_events: number },
 *     severity_breakdown?: Array<{ severity: string, count: number }>,
 *     event_types?: Array<{ event_type: string, count: number }>,
 *     recent_activity?: Array<{ event_date: string, daily_count: number }>,
 *     top_projects?: Array<{ project_id: number, project_name: string, event_count: number }>,
 *     resolution_metrics?: { resolved_count: number, avg_resolution_hours: number, avg_resolution_hours_recent: number }
 *   },
 *   isLoading: boolean,
 *   refetch: () => Promise<any>,
 *   error: any
 * }}
 */
export default function useOrganisationEventMetrics(event_level, config = {}) {
  const { organisationData } = useOrgContext();
  
  const fetchOrganisationEventMetrics = async (token) => {
    const requestBody = {
      org_id: organisationData?.id,
      event_level: event_level,
      config: config
    };

    try {
      const response = await axios.post(`${API_BASE_URL}events/organisation/metrics`, requestBody, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-functions-key': FUNCTION_KEY,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      logError('Failed to load organisation event metrics in useOrganisationEventMetrics', error, requestBody);
      throw error;
    }
  };

  const projectScope = [PROJECT_SCOPE];
  
  // Create a query key that includes all the parameters that could affect the result
  const queryKey = [
    'organisationEventMetrics', 
    organisationData?.id, 
    event_level, 
    JSON.stringify(config)
  ];

  const { 
    data: metrics = {}, 
    isLoading, 
    refetch, 
    error 
  } = useAuthenticatedQuery(
    queryKey,
    fetchOrganisationEventMetrics, 
    projectScope,
    {
      enabled: !!organisationData?.id && !!event_level,
      staleTime: 5 * 60 * 1000,  // Data will be cached for 5 minutes
    }
  );

  return { metrics, isLoading, refetch, error };
}

useOrganisationEventMetrics.propTypes = {
  event_level: PropTypes.oneOf(['Project', 'Test']).isRequired,
  config: PropTypes.shape({
    recent_days: PropTypes.number,
    top_projects_limit: PropTypes.number,
    long_running_hours: PropTypes.number,
    trend_days: PropTypes.number
  })
};